import { gql, useQuery } from '@apollo/client';

import { Server } from '../../apollo/types';

export const PRESCRIBER_EPRESCRIBE_STATUS = {
  DISABLED: 'DISABLED',
  OPT_IN: 'OPT_IN',
  NOT_SYNCED: 'NOT_SYNCED',
  INCOMPLETE: 'INCOMPLETE',
  CONFIRMED: 'CONFIRMED',
  IDP_COMPLETED: 'IDP_COMPLETED',
} as const;

export type PrescriberEprescribeStatus =
  (typeof PRESCRIBER_EPRESCRIBE_STATUS)[keyof typeof PRESCRIBER_EPRESCRIBE_STATUS];

type GetPrescriberEprescribeStatusQueryData = {
  prescriberEprescribeStatus: PrescriberEprescribeStatus | null;
};

export const GET_PRESCRIBER_EPRESCRIBE_STATUS = gql`
  query GetPrescriberEprescribeStatus {
    prescriberEprescribeStatus
  }
`;

interface UsePrescriberEprescribeStatusResult {
  /** Null for a non-prescriber, a failed read, and while the status is loading. */
  status: PrescriberEprescribeStatus | null;
  loading: boolean;
}

/**
 * The logged-in prescriber's ePrescribe status.
 *
 * Skipped for non-prescribers. Each mount revalidates the cached status, so a
 * prescriber who finishes identity proofing sees the medications card the next
 * time the client drawer opens.
 */
export function usePrescriberEprescribeStatus(isPrescriber: boolean): UsePrescriberEprescribeStatusResult {
  const {
    data,
    error,
    loading: queryLoading,
  } = useQuery<GetPrescriberEprescribeStatusQueryData>(GET_PRESCRIBER_EPRESCRIBE_STATUS, {
    context: { server: Server.MONOLITH },
    skip: !isPrescriber,
    fetchPolicy: 'cache-and-network',
  });

  return {
    status: error ? null : (data?.prescriberEprescribeStatus ?? null),
    loading: isPrescriber && queryLoading && !data,
  };
}
